/**
 * Professions Query Hooks
 * Hooks for fetching professions data
 */

import { ProfessionsResponse } from '@/shared/types/models'
import { apiClient } from '@shared/lib/api/client'
import { API_ENDPOINTS } from '@shared/lib/api/endpoints'
import type { Profession } from '@shared/types'
import { useQuery } from '@tanstack/react-query'

// ============================================================================
// Query Keys
// ============================================================================

export const professionsKeys = {
  all: ['professions'] as const,
  lists: () => [...professionsKeys.all, 'list'] as const,
  list: (filters?: string) => [...professionsKeys.lists(), filters] as const,
  details: () => [...professionsKeys.all, 'detail'] as const,
  detail: (id: string) => [...professionsKeys.details(), id] as const,
}

// ============================================================================
// Queries
// ============================================================================

/**
 * Get all professions with professional count
 * Returns list of professions sorted by order
 */
export function useProfessions() {
  return useQuery({
    queryKey: professionsKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get<ProfessionsResponse>(API_ENDPOINTS.professions.base)
      // Sort by order field
      return response.professions.sort((a, b) => a.order - b.order)
    },
    staleTime: 5 * 60 * 1000, // 5 minutes (professions don't change often)
  })
}

/**
 * Get single profession by ID
 */
export function useProfession(id: string) {
  return useQuery({
    queryKey: professionsKeys.detail(id),
    queryFn: () => apiClient.get<Profession>(API_ENDPOINTS.professions.byId(id)),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}
